import { Link } from "react-router-dom";
import { ArrowRight, Target, PlayCircle, Lightbulb, MessagesSquare, Dumbbell, Compass } from "lucide-react";
import { ResCol, SectionBody, Expandable, Steps, splitLines } from "@/components/network/parts";
import RichBlock from "@/components/vetting/RichBlock";
import { PrimaryZone } from "@/components/vetting/ResourceCard";
import { RunsText } from "@/components/appointments/MessageCard";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const len = (runs) => (runs || []).map((r) => r.t).join("").length;

function Block({ icon: Icon, title, children, testid }) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <h3 className="font-serif text-xl text-[#0A1F44] flex items-center gap-2"><Icon className="w-5 h-5 text-[#C9A961]" />{title}</h3>
      {children}
    </section>
  );
}

function Card({ sec, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">{sec.label}</div>
      <SectionBody sec={sec} testid={testid} />
    </div>
  );
}

function StudyCard({ sec, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-xs font-semibold text-[#0A1F44]">{sec.label}</div>
      {len(sec.body) > 0 && sec.kind !== "purpose" && <RichBlock runs={sec.body} testid={`${testid}-desc`} />}
      <ResCol sec={sec} testid={testid} />
    </div>
  );
}

function FiveStages({ skill, sec }) {
  return (
    <section className="rounded-2xl border border-[#C9A961]/40 bg-white p-4 sm:p-5 space-y-4" data-testid="net-five-stages">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">{sec.label}</div>
      <div className="space-y-1" data-testid="net-five-intro">
        {splitLines(sec.body).filter((l) => l.length && !/^\s*\d\./.test(l.map((r) => r.t).join(""))).map((l, i) => <RunsText key={i} runs={l} />)}
      </div>
      <div className="flex flex-col gap-2">
        {skill.five_stages.map((st) => (
          <Link key={st.n} to={`/network/${st.skill}`} data-testid={`net-stage-btn-${st.n}`}
                className="group rounded-xl bg-[#0A1F44] text-white p-3 hover:bg-[#0A1F44]/90 transition-colors flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-[#C9A961] text-[#0A1F44] text-xs font-bold flex items-center justify-center shrink-0">{st.n}</span>
            <span className="text-xs leading-snug flex-1">{st.text.replace(/^\d+\.\s*/, "")}</span>
            <ArrowRight className="w-4 h-4 text-[#C9A961] shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        ))}
      </div>
      <ResCol sec={sec} testid="net-flowchart" />
    </section>
  );
}

export default function SkillPage({ skill }) {
  const grouped = new Set((skill.groups || []).flatMap((g) => g.sections));
  const secs = skill.sections.map((s, i) => ({ ...s, i })).filter((s) => !grouped.has(s.i));
  const of = (...kinds) => secs.filter((s) => kinds.includes(s.kind));
  const purposes = of("purpose");
  const study = [...purposes.filter((s) => len(s.res) || s.missing?.length), ...of("resource")];
  const principles = of("principle");
  const examples = of("example", "example_resource");
  const practice = of("practice", "steps");
  const next = of("next");
  const stages = of("stages")[0];
  return (
    <div className="space-y-8">
      {purposes.map((s) => (
        <section key={s.i} className="rounded-2xl border-l-4 border-[#C9A961] bg-[#F7F5EF] p-4 sm:p-5" data-testid={`net-purpose-${slug(s.label)}`}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold mb-1.5"><Target className="w-3.5 h-3.5" />{s.label}</div>
          <RichBlock runs={s.body} testid={`net-purpose-${slug(s.label)}-body`} />
        </section>
      ))}
      {stages && <FiveStages skill={skill} sec={stages} />}
      {study.length > 0 && (
        <Block icon={PlayCircle} title="Watch & study" testid="net-study">
          {/* Layout rule: Building Your Network main content is always ONE column — no side-by-side topics/resources. */}
          <div className="flex flex-col gap-6">{study.map((s, si) => <PrimaryZone key={s.i} on={si < 2}><StudyCard sec={s} testid={`net-study-${slug(s.label)}`} /></PrimaryZone>)}</div>
        </Block>
      )}
      {principles.length > 0 && (
        <Block icon={Lightbulb} title="Key principles" testid="net-principles">
          <div className="space-y-3">
            {principles.map((s) => (len(s.body) > 900
              ? <Expandable key={s.i} label={s.label} testid={`net-principle-${slug(s.label)}`}><SectionBody sec={s} testid={`net-principle-${slug(s.label)}-in`} /></Expandable>
              : <Card key={s.i} sec={s} testid={`net-principle-${slug(s.label)}`} />))}
          </div>
        </Block>
      )}
      {skill.groups?.length > 0 && (
        <Block icon={MessagesSquare} title="Common questions — adaptable examples" testid="net-groups">
          <p className="text-xs text-slate-500">Examples to adapt in your own words — nothing here is sent automatically.</p>
          <div className="space-y-2">
            {skill.groups.map((g) => (
              <Expandable key={g.label} label={g.label} testid={`net-group-${slug(g.label)}`}>
                {g.sections.map((i) => {
                  const s = skill.sections[i];
                  return (
                    <div key={i} className="space-y-2">
                      {i !== g.sections[0] && <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">{s.label}</div>}
                      <SectionBody sec={s} testid={`net-group-sec-${i}`} />
                    </div>
                  );
                })}
              </Expandable>
            ))}
          </div>
        </Block>
      )}
      {examples.length > 0 && (
        <Block icon={MessagesSquare} title="Examples & questions" testid="net-examples">
          <div className="space-y-2">
            {examples.map((s) => (
              <Expandable key={s.i} label={s.label} testid={`net-example-${slug(s.label)}`} defaultOpen={s.kind === "example_resource"}>
                <SectionBody sec={s} testid={`net-example-${slug(s.label)}-in`} />
              </Expandable>
            ))}
          </div>
        </Block>
      )}
      {practice.length > 0 && (
        <Block icon={Dumbbell} title="Put it into practice" testid="net-practice">
          <div className="space-y-3">
            {practice.map((s) => (
              <div key={s.i} className="rounded-xl border border-[#0A1F44]/15 bg-[#0A1F44]/[0.03] p-4 space-y-2" data-testid={`net-practice-${slug(s.label)}`}>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">{s.label}</div>
                {s.kind === "steps" ? <Steps sec={s} testid="net-php-steps" /> : <SectionBody sec={s} testid={`net-practice-${slug(s.label)}-in`} />}
              </div>
            ))}
          </div>
        </Block>
      )}
      {(next.length > 0 || skill.cross_links?.length > 0) && (
        <Block icon={Compass} title="Next step" testid="net-next">
          {next.map((s) => <Card key={s.i} sec={s} testid={`net-next-${slug(s.label)}`} />)}
          <div className="flex flex-wrap gap-2">
            {(skill.cross_links || []).map((c) => (
              <Link key={c.to} to={c.to} data-testid={`net-cross-${slug(c.to)}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A961] text-[#0A1F44] text-xs font-semibold px-3 py-1.5 hover:bg-[#C9A961]/10">
                {c.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ))}
          </div>
        </Block>
      )}
    </div>
  );
}
